/**
 * Containernummers: normalisatie en het groeperen van verzendingen per container.
 *
 * Dit bestand bevat opzettelijk GEEN Firestore- of React-afhankelijkheden: alles hier is pure
 * logica die los te testen is (zie containerNumber.test.js).
 *
 * Waarom dit meer is dan cosmetica: de detailpagina koppelt verzendingen aan elkaar op hun
 * containernummer, en die koppeling voedt de knop "Pre-order datums" waarmee in één keer alle
 * producten van een hele container op pre-order worden gezet. Een containernummer dat de ene
 * keer als "MSKU 123-456" en de andere keer als "msku123456" is ingevoerd, splitst die container
 * dus stilletjes in tweeën — en de bulkactie raakt dan maar de helft van de verzendingen.
 */

/**
 * Normaliseert een containernummer: hoofdletters, geen spaties, geen streepjes (BMX-21).
 *
 * Bewust NIET meer dan dat. Punten en schuine strepen blijven staan: die worden in het ticket
 * niet genoemd, en alles wegstrippen wat geen letter of cijfer is kan twee werkelijk verschillende
 * nummers op één hoop gooien. Naast de gewone spatie/koppelteken vangen we ook de varianten die
 * uit Excel of een PDF geplakt worden (harde spatie, en-dash, em-dash, minusteken) — dat is
 * precies het geval waarin een mens twee nummers als gelijk leest en de computer niet.
 *
 * @param {*} value - ruwe invoer, mag null/undefined zijn
 * @returns {string} genormaliseerd nummer, of "" als er niets overblijft
 */
export const normalizeContainerNumber = (value) =>
  String(value ?? "")
    // \s dekt in JS ook de harde spatie (U+00A0) en de overige unicode-spaties.
    .replace(/\s/g, "")
    .replace(/[-‐-―−]/g, "")
    .toUpperCase();

/**
 * Wat er daadwerkelijk in Firestore terechtkomt: het genormaliseerde nummer, of `null` als het
 * veld leeg is. `null` en niet `""`, omdat de rest van de code (en de bestaande data) een leeg
 * containernummer als `null` opslaat.
 */
export const containerNumberForStorage = (value) => normalizeContainerNumber(value) || null;

/**
 * Horen twee containernummers bij dezelfde container? Vergelijkt op de genormaliseerde waarde,
 * zodat verzendingen die vóór BMX-21 zijn ingevoerd (of via het leveranciersportaal binnenkomen)
 * alsnog aan elkaar gekoppeld worden zonder dat de bestaande data aangepast hoeft te worden.
 * Een leeg nummer hoort nooit bij een container — anders zou elke verzending zonder container
 * bij elke andere verzending zonder container horen.
 */
export const isSameContainer = (a, b) => {
  const left = normalizeContainerNumber(a);
  return left !== "" && left === normalizeContainerNumber(b);
};

/**
 * Alle verzendingen die in dezelfde container zitten als `containerNumber`, behalve de
 * verzending die je zelf bekijkt (`excludeId`).
 */
export const findContainerSiblings = (shipments, containerNumber, excludeId) => {
  if (!normalizeContainerNumber(containerNumber)) return [];
  return (shipments || []).filter((s) => s && s.id !== excludeId && isSameContainer(s.containerNumber, containerNumber));
};

/**
 * De lijst met bestaande containernummers voor de autocomplete: uniek op genormaliseerde waarde,
 * alfabetisch gesorteerd, met per nummer het aantal verzendingen erachter. Dat aantal is wat de
 * gebruiker laat zien dát een container al bestaat, nog vóór hij opslaat.
 *
 * @returns {Array<{ value: string, count: number }>}
 */
export const collectContainerNumbers = (shipments) => {
  const counts = new Map();
  (shipments || []).forEach((s) => {
    const normalized = normalizeContainerNumber(s && s.containerNumber);
    if (!normalized) return;
    counts.set(normalized, (counts.get(normalized) || 0) + 1);
  });
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => a.value.localeCompare(b.value));
};

/**
 * Filtert die lijst op wat de gebruiker tot nu toe getypt heeft. De zoekterm gaat door dezelfde
 * normalisatie, zodat iemand die "msku 12" typt gewoon "MSKU1234567" te zien krijgt.
 */
export const filterContainerNumbers = (containerNumbers, search) => {
  const needle = normalizeContainerNumber(search);
  if (!needle) return containerNumbers || [];
  return (containerNumbers || []).filter((c) => c.value.includes(needle));
};
